import { isValidElement } from "react";
import { toast } from "sonner";
import { safeErrorText } from "@/lib/safeErrorText";

/**
 * installToastGuard — wraps every sonner `toast.*` entry point so a
 * non-renderable `title` / `description` (e.g. a raw Pydantic 422 `detail`
 * array forwarded from an axios error) is stringified BEFORE it reaches
 * the <Toaster>. The Toaster lives OUTSIDE the app ErrorBoundary, so a
 * render throw there ("Objects are not valid as a React child") unmounts
 * the whole React root → users see only the purple page background.
 * Idempotent; call once at app boot.
 */
const METHODS = ["success", "info", "warning", "error", "message", "loading"];

function renderable(v) {
  return v == null || typeof v === "string" || typeof v === "number" || typeof v === "function" || isValidElement(v);
}

function sanitize(v, fallback) {
  return renderable(v) ? v : safeErrorText(v, fallback);
}

function sanitizeOpts(opts) {
  if (!opts || typeof opts !== "object" || Array.isArray(opts)) return opts;
  if (renderable(opts.description)) return opts;
  return { ...opts, description: sanitize(opts.description, "") };
}

export function installToastGuard() {
  if (toast.__elpmGuarded) return;
  for (const m of METHODS) {
    const orig = toast[m];
    if (typeof orig !== "function") continue;
    toast[m] = (title, opts) => orig(sanitize(title, "Something happened"), sanitizeOpts(opts));
  }
  toast.__elpmGuarded = true;
}
